import { useState } from "react";
import useDatasetTables from "datasets/pages/tables/hooks/useDatasetTables";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import Dataset from "datasets/types/interfaces/dataset";
import { Search, X } from "lucide-react";
import { TablePreviewModal } from "~/components/table-preview/TablePreviewModal";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { DatasetTable } from "~/pages/manifold-nextgen/components/datasets/pages/tables/DatasetTable";

interface SessionContextDatasetTablesProps {
  dataset: Dataset;
}

const TableSkeleton = () => (
  <div role="status" aria-label="Loading tables" className="flex flex-col gap-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-16 w-full rounded-md" />
    ))}
  </div>
);

const TableRow = ({
  table,
  onSelect,
  onOpenPreview,
}: {
  table: DatasetTableResponse;
  onSelect: (table: DatasetTableResponse) => void;
  onOpenPreview: (table: DatasetTableResponse) => void;
}) => (
  <div className="flex items-start gap-3 border-b border-border py-4 last:border-0">
    <button
      type="button"
      onClick={() => onSelect(table)}
      className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1 text-left"
    >
      <span className="text-sm font-semibold text-primary">{table.name}</span>
      <span className="text-xs text-muted-foreground">{table.database_schema}</span>
      <span className="line-clamp-2 text-sm text-muted-foreground">
        {table.description || <span className="italic">No description</span>}
      </span>
    </button>
    <Button variant="outline" size="sm" className="shrink-0" onClick={() => onOpenPreview(table)}>
      View Data
    </Button>
  </div>
);

export const SessionContextDatasetTables = ({ dataset }: SessionContextDatasetTablesProps) => {
  const [search, setSearch] = useState("");
  const { data: tables, isLoading } = useDatasetTables(search, dataset.id);
  const [previewTable, setPreviewTable] = useState<DatasetTableResponse | null>(null);
  const [selectedTableFQN, setSelectedTableFQN] = useState<string | null>(null);

  if (selectedTableFQN) {
    return (
      <DatasetTable
        datasetId={dataset.id}
        tableFQN={selectedTableFQN}
        sessionSidebarView
        onBack={() => setSelectedTableFQN(null)}
      />
    );
  }

  const showEmpty = !isLoading && (!tables || tables.length === 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input
          placeholder="Search tables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 pr-8"
        />
        {search && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1 size-7"
            onClick={() => setSearch("")}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : showEmpty ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {search ? "No tables match your search." : "No tables in this dataset."}
        </p>
      ) : (
        <div>
          {tables?.map((table) => (
            <TableRow
              key={table.id}
              table={table}
              onSelect={(t) => setSelectedTableFQN(t.fullyQualifiedName)}
              onOpenPreview={setPreviewTable}
            />
          ))}
        </div>
      )}

      <TablePreviewModal
        open={!!previewTable}
        onClose={() => setPreviewTable(null)}
        tableFqn={previewTable?.fullyQualifiedName ?? ""}
        tableName={previewTable?.name ?? ""}
      />
    </div>
  );
};
